import { EmployeeApiMapperType } from './types';
import {
  EmployeeRequestDto,
  EmployeeDetailDto,
  EmployeeDto,
  EmployeeUpdateDto,
  EmployeeCreateDto,
} from 'domains/personnel/application/dtos';
import {
  ApiEmployeeListRequest,
  ApiEmployeeUpdateRequest,
  ApiEmployeeCreateRequest,
} from '@personnel/infrastructure/api/types/EmployeeApiRequest';
import { EmployeeApiResponseDto } from 'domains/personnel/application/dtos';
import {
  EmployeeApiResponse,
  EmployeeUpdateResponse,
  EmployeeCreateSuccessResponse,
} from '@personnel/infrastructure/api/types/index';
import { ApiEmployee, ApiEmployeeDetail } from '@personnel/infrastructure/api/types/EmployeeApiResponse';

/**
 * Factory that creates an API mapper for personnel employees
 */
export const createEmployeeApiMapper = (): EmployeeApiMapperType => {
  /**
   * Maps EmployeeRequestDto to ApiEmployeeListRequest
   *
   * @param {EmployeeRequestDto} request - Application layer DTO
   * @returns {ApiEmployeeListRequest} API request payload
   */
  const mapEmployeeRequestDtoToApiRequest = (request: EmployeeRequestDto): ApiEmployeeListRequest => {
    return {
      page: request.page,
      limit: request.limit,
      search: request.search,
      roleUuids: request.roleUuids,
      storeUuids: request.storeUuids,
      verificationStatus: request.verificationStatus?.length ? request.verificationStatus : undefined,
      includeAssignedStores: request.includeAssignedStores,
    };
  };

  const mapEmployeeToDto = (employee: ApiEmployee): EmployeeDto => ({
    uuid: employee.uuid,
    fullName: `${employee.firstName} ${employee.lastName}`.trim(),
    firstName: employee.firstName,
    lastName: employee.lastName,
    email: employee.email,
    roleUuid: employee.roleUuid,
    posPin: employee.posPin,
    isActive: employee.isActive,
    organizationUuid: employee.organizationUuid,
    phoneNumber: employee.phoneNumber,
    roleName: employee.roleName,
    permissions: employee.permissions,
    profileUuid: employee.profileUuid,
    assignedStores: employee.assignedStores?.length,
    allStoresAssigned: employee.allStoresAssigned,
    verificationStatus: employee.verificationStatus,
    notificationPreferences: employee.notificationPreferences,
    createdAt: employee.createdAt,
    updatedAt: employee.updatedAt,
    lastLoginAt: employee.lastLoginAt,
  });

  const mapEmployeeResponseToDto = (response: EmployeeApiResponse): EmployeeApiResponseDto => {
    return {
      data: response.data.map(mapEmployeeToDto),
      pagination: response.pagination,
    };
  };

  const mapEmployeeDetailResponseToDto = (employee: ApiEmployeeDetail): EmployeeDetailDto => ({
    uuid: employee.uuid,
    email: employee.email,
    fullName: `${employee.firstName} ${employee.lastName}`.trim(),
    firstName: employee.firstName,
    lastName: employee.lastName,
    phoneNumber: employee.phoneNumber,
    hasSetEmailAndPassword: employee.hasSetEmailAndPassword ?? false,
    posPin: employee.posPin,
    isActive: employee.isActive,
    roleUuid: employee.roleUuid,
    roleName: employee.roleName ?? '',
    assignedStores: employee.assignedStores ?? [],
    allStoresAssigned: !!employee.allStoresAssigned,
    verificationStatus: employee.verificationStatus,
    verificationRequirements: employee.verificationRequirements,
    notificationPreferences: employee.notificationPreferences,
    profileId: employee.profileId,
  });

  /**
   * Maps EmployeeCreateDto to ApiEmployeeCreateRequest
   *
   * @param {EmployeeCreateDto} request - Application layer DTO for employee creation
   * @returns {ApiEmployeeCreateRequest} API request payload
   */
  const mapEmployeeCreateDtoToApiRequest = (request: EmployeeCreateDto): ApiEmployeeCreateRequest => ({
    firstName: request.firstName,
    lastName: request.lastName,
    email: request.email,
    profileId: request.profileId,
    posPin: request.posPin,
    roleUuid: request.roleUuid,
    assignedStores: [...request.assignedStores],
    allStoresAssigned: request.allStoresAssigned,
    notificationPreferences: request.notificationPreferences,
  });

  /**
   * Maps EmployeeCreateSuccessResponse to EmployeeDto
   * Reuses mapEmployeeToDto for consistency
   *
   * @param {EmployeeCreateSuccessResponse} response - API response after creation
   * @returns {EmployeeDto} Application layer DTO
   */
  const mapEmployeeCreateResponseToDto = (response: EmployeeCreateSuccessResponse): EmployeeDto => {
    return mapEmployeeToDto(response.data);
  };

  /**
   * Maps EmployeeUpdateDto to ApiEmployeeUpdateRequest
   *
   * The intent and the two change flags of the DTO stay behind: they describe the save for the log
   * and the backend has no key for them.
   *
   * @param {EmployeeUpdateDto} request - Application layer DTO for the update
   * @returns {ApiEmployeeUpdateRequest} API request payload
   */
  const mapEmployeeUpdateDtoToApiRequest = (request: EmployeeUpdateDto): ApiEmployeeUpdateRequest => ({
    userUuid: request.userUuid,
    firstName: request.firstName,
    lastName: request.lastName,
    email: request.email,
    profileId: request.profileId,
    posPin: request.posPin,
    roleUuid: request.roleUuid,
    assignedStores: request.assignedStores,
    allStoresAssigned: request.allStoresAssigned,
    notificationPreferences: request.notificationPreferences,
  });

  const mapEmployeeUpdateResponseToDto = (response: EmployeeUpdateResponse): EmployeeDto => {
    return mapEmployeeToDto(response.data);
  };

  return {
    mapEmployeeRequestDtoToApiRequest,
    mapEmployeeResponseToDto,
    mapEmployeeDetailResponseToDto,
    mapEmployeeCreateDtoToApiRequest,
    mapEmployeeCreateResponseToDto,
    mapEmployeeUpdateDtoToApiRequest,
    mapEmployeeUpdateResponseToDto,
  };
};
